import { Controller } from "@hotwired/stimulus"
import { fetchTurboStream } from "../../src/shared/helpers/turbo_stream_fetch"
import { internationalPhone } from "../../src/shared/helpers/international_phone"

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// "06 12 34 56 78"; with a prefix, the shortest offered is Andorra's "+376 312 345".
const MIN_PHONE_DIGITS = 10
const MIN_INTERNATIONAL_PHONE_DIGITS = 9

// Practitioner create: while a new account is typed, asks the server whether
// its email or phone already belongs to a user. The answer streams into this
// element (a notice offering that account, or nothing). Nothing is asked while
// an existing account is picked.
export default class extends Controller {
  static values = { url: String, emailField: String, phoneField: String, delay: { type: Number, default: 500 } }

  connect () {
    this.form = this.element.closest("form")
    if (!this.form) return

    this.schedule = this.schedule.bind(this)
    this.form.addEventListener("input", this.schedule)
    this.form.addEventListener("click", this.schedule)
  }

  disconnect () {
    clearTimeout(this.timeout)
    this.form?.removeEventListener("input", this.schedule)
    this.form?.removeEventListener("click", this.schedule)
  }

  schedule () {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.check(), this.delayValue)
  }

  async check () {
    const query = this.query()
    if (query === this.lastQuery) return

    this.lastQuery = query
    if (!query) {
      this.element.replaceChildren()
      return
    }

    await fetchTurboStream(`${this.urlValue}?${query}`)
  }

  query () {
    const email = this.shownValue(this.emailFieldValue)
    if (email === null) return ""

    const phone = this.shownPhone()
    const minDigits = phone.startsWith("+") ? MIN_INTERNATIONAL_PHONE_DIGITS : MIN_PHONE_DIGITS
    const params = new URLSearchParams()
    if (EMAIL_FORMAT.test(email)) params.set("email", email)
    if (phone.replace(/\D/g, "").length >= minDigits) params.set("phone", phone)

    return params.toString()
  }

  // With the country picked on the flag, as the form will send it: a Belgian
  // "0470…" must not be looked up as a French number.
  shownPhone () {
    const phone = this.shownValue(this.phoneFieldValue) || ""
    const dialCode = document.getElementById(this.phoneFieldValue)?.dataset.phoneDialCode

    return internationalPhone(phone, dialCode)
  }

  // null when the field is hidden: the admin picked an existing account.
  shownValue (fieldId) {
    const field = document.getElementById(fieldId)
    if (!field || field.disabled || field.closest(".d-none, [hidden]")) return null

    return field.value.trim()
  }
}
